import { Box3, InstancedMesh, Matrix4, Mesh, Ray } from 'three';
import type { BufferGeometry, InstancedBufferAttribute, Intersection, Raycaster } from 'three';
import { MeshBVH, acceleratedRaycast } from 'three-mesh-bvh';
import { AabbRayIndex } from '../spatial';

/**
 * 为一个正式 InstancedMesh 建立“局部槽位 AABB → 共享几何”两级拾取。
 *
 * 本对象只拥有 CPU 索引与离屏求交代理，不拥有业务 Mesh、共享几何或材质。
 * 命中继续返回原 InstancedMesh 和 instanceId，由既有注册表解析稳定 occurrence 身份。
 */
export class InstancedMeshRaycastIndex {
  /** 实例组局部空间的槽位边界；父级变换只需变换射线。 */
  private readonly index = new AabbRayIndex();
  /** 只在本批实际变化的槽位。 */
  private readonly dirtyInstances = new Set<number>();
  /** 求交候选缓冲，不为每次输入分配数组。 */
  private readonly candidates: number[] = [];
  /** 单槽精确求交缓冲，命中对象交给调用方后清空引用。 */
  private readonly preciseHits: Intersection[] = [];
  /** 每个实例组仅创建一次、不挂场景的共享几何代理。 */
  private preciseMesh: Mesh | null;
  /** 原生 raycast，释放时恢复；不修改全局 Three.js 原型。 */
  private readonly originalRaycast: InstancedMesh['raycast'];
  /** 原始对象是否自有 raycast，释放时还原属性归属。 */
  private readonly hadOwnRaycast: boolean;
  /** 当前几何身份及属性版本用于识别未通过槽位入口的几何变化。 */
  private geometry: BufferGeometry | null = null;
  /** 最近已确认的顶点属性引用。 */
  private position: BufferGeometry['attributes'][string] | undefined;
  /** 顶点属性版本，兼容交错缓冲。 */
  private positionVersion = -1;
  /** 最近已确认的三角索引引用。 */
  private geometryIndex: BufferGeometry['index'] = null;
  /** 最近已确认的三角索引版本。 */
  private geometryIndexVersion = -1;
  /** 上次发布的实例缓冲引用，替换时保守重算槽位边界。 */
  private matrixArray: InstancedBufferAttribute['array'] | null = null;
  /** 上次发布的实例缓冲版本。 */
  private matrixVersion = -1;
  /** 当前空间索引结构对应的活动槽位数。 */
  private instanceCount = -1;
  /** 观察到几何变化但尚未确认替换的旧共享 BVH，禁止继续用于精确求交。 */
  private staleBoundsTree: BufferGeometry['boundsTree'];
  /** 只缓存用于检测 BVH 构建范围变化的纯数字，不引用可变 groups 数组。 */
  private readonly geometryGroups: number[] = [];
  /** 上次观测的绘制范围起点。 */
  private drawStart = -1;
  /** 上次观测的绘制范围长度。 */
  private drawCount = -1;
  /** 复用实例组逆矩阵。 */
  private readonly inverseMatrix = new Matrix4();
  /** 直接读取与 GPU 相同的 Float32 实例矩阵，避免另一套位姿精度。 */
  private readonly instanceMatrix = new Matrix4();
  /** 复用局部射线。 */
  private readonly localRay = new Ray();
  /** 单槽包围计算临时值，写入索引后不保留引用。 */
  private readonly bounds = new Box3();
  /** 已经实际重算的槽位边界总数。 */
  private refreshedBounds = 0;
  /** 空间数据发布版本；相机只比较该数字，不读取每个实例矩阵。 */
  private boundsRevision = 0;
  /** 最近一次查询实际精确求交槽位数。 */
  private lastPreciseTests = 0;
  /** 精确求交累计槽位数。 */
  private preciseTests = 0;

  /**
   * 安装实例组专属的拾取入口；不覆盖已有业务自定义 raycast。
   * @param object 当前运行时实例组，调用方仍负责它的生命周期。
   */
  public constructor(private object: InstancedMesh | null) {
    if (!object || object.raycast !== InstancedMesh.prototype.raycast) {
      throw new Error('实例拾取索引只能接管采用原生 raycast 的 InstancedMesh');
    }
    this.originalRaycast = object.raycast;
    this.hadOwnRaycast = Object.prototype.hasOwnProperty.call(object, 'raycast');
    this.preciseMesh = new Mesh(object.geometry, object.material);
    this.preciseMesh.matrixAutoUpdate = false;
    object.raycast = this.raycast;
  }

  /**
   * 登记已写入矩阵的槽位；一个事务中重复登记会合并。
   * @param instanceIndex 当前活动实例下标，GPU 重分桶必须使用新索引所有者。
   */
  public markInstanceDirty(instanceIndex: number): void {
    if (!this.object) return;
    if (!Number.isInteger(instanceIndex) || instanceIndex < 0 || instanceIndex >= this.object.count) {
      throw new Error(`实例拾取索引槽位越界：${instanceIndex}`);
    }
    this.dirtyInstances.add(instanceIndex);
  }

  /**
   * 发布矩阵变化并校正空间树，不执行拾取或渲染。
   *
   * 正规 Unit 调用链提供精确 dirty 槽；低层直接改 buffer 且没有槽位声明时按版本全量校验。
   */
  public commitMatrixChanges(): void {
    const object = this.object;
    if (!object) return;
    const geometry = object.geometry;
    const position = geometry.getAttribute('position');
    const positionVersion = position && ('data' in position ? position.data.version : position.version);
    const geometryChanged = this.geometry !== geometry || this.position !== position
      || this.positionVersion !== (positionVersion ?? -1) || this.geometryIndex !== geometry.index
      || this.geometryIndexVersion !== (geometry.index?.version ?? -1);
    const structureChanged = this.instanceCount !== object.count;
    const bufferReplaced = this.matrixArray !== object.instanceMatrix.array;
    const untrackedMatrixChanges = this.matrixVersion !== object.instanceMatrix.version && this.dirtyInstances.size === 0;
    const boundsChanged = structureChanged || bufferReplaced || geometryChanged || untrackedMatrixChanges
      || this.dirtyInstances.size > 0;
    if (geometryChanged) {
      // 同一 geometry 的属性变化不能再信任旧包围或旧 BVH；几何树由其原所有者重建。
      this.staleBoundsTree = this.geometry === geometry ? geometry.boundsTree : undefined;
      geometry.computeBoundingBox();
      geometry.computeBoundingSphere();
      this.position = position;
      this.positionVersion = positionVersion ?? -1;
      this.geometryIndex = geometry.index;
      this.geometryIndexVersion = geometry.index?.version ?? -1;
    }
    this.refreshGeometryRange(geometry);
    this.geometry = geometry;
    if (structureChanged) this.index.reset(object.count);
    if (structureChanged || bufferReplaced || geometryChanged || untrackedMatrixChanges) {
      for (let instanceId = 0; instanceId < object.count; instanceId++) this.refreshInstanceBounds(instanceId);
    } else {
      for (const instanceId of this.dirtyInstances) this.refreshInstanceBounds(instanceId);
    }
    this.instanceCount = object.count;
    this.matrixArray = object.instanceMatrix.array;
    this.matrixVersion = object.instanceMatrix.version;
    this.dirtyInstances.clear();
    this.index.flush();
    if (boundsChanged) this.boundsRevision++;
  }

  /**
   * 校验缓冲/几何版本并读取空间修订；无变化时不重新计算槽位范围。
   * @returns 当前发布版本。
   */
  public getBoundsRevision(): number {
    this.commitMatrixChanges();
    return this.boundsRevision;
  }

  /**
   * 读取已发布的局部槽位盒；批量读取前由消费者调用 getBoundsRevision 校验一次。
   * @param instanceIndex 正式槽位。
   * @param target 消费者复用缓冲。
   * @returns 是否存在可靠盒。
   */
  public copyInstanceBounds(instanceIndex: number, target: Box3): boolean {
    return this.index.copyBounds(instanceIndex, target);
  }

  /** @returns 显式读取的工作量快照，不在射线热路径创建统计对象。 */
  public getStats(): InstancedMeshRaycastStats {
    return { instances: this.object?.count ?? 0, refreshedBounds: this.refreshedBounds,
      lastPreciseTests: this.lastPreciseTests, preciseTests: this.preciseTests, index: this.index.getStats() };
  }

  /** 幂等恢复对象原始拾取入口并解除引用，不释放共享几何、材质或正式实例对象。 */
  public dispose(): void {
    if (!this.object) return;
    if (this.object.raycast === this.raycast) {
      if (this.hadOwnRaycast) this.object.raycast = this.originalRaycast;
      else Reflect.deleteProperty(this.object, 'raycast');
    }
    this.object = this.preciseMesh = this.geometry = null;
    this.position = undefined;
    this.geometryIndex = this.matrixArray = null;
    this.staleBoundsTree = undefined;
    this.candidates.length = this.preciseHits.length = this.geometryGroups.length = 0;
    this.dirtyInstances.clear();
    this.index.dispose();
  }

  /**
   * 只把局部 AABB 命中的槽位交给精确几何求交；仍由外层 Raycaster 排序全部对象距离。
   * @param raycaster 既有选择管线的射线与 near/far。
   * @param intersects 外层结果缓冲，命中保留原实例组与实例下标。
   */
  private readonly raycast = (raycaster: Raycaster, intersects: Intersection[]): void => {
    const object = this.object;
    const preciseMesh = this.preciseMesh;
    this.lastPreciseTests = 0;
    if (!object || !preciseMesh || object.material === undefined) return;
    this.commitMatrixChanges();
    // 特殊顶点变形不具备静态槽位边界，沿用原始实现，不以错误粗包围过滤。
    if (object.morphTexture || object.geometry.morphAttributes.position?.length || object.matrixWorld.determinant() === 0) {
      this.originalRaycast.call(object, raycaster, intersects);
      return;
    }
    this.inverseMatrix.copy(object.matrixWorld).invert();
    this.localRay.copy(raycaster.ray).applyMatrix4(this.inverseMatrix);
    this.index.query(this.localRay, this.candidates);
    preciseMesh.geometry = object.geometry;
    preciseMesh.material = object.material;
    const tree = object.geometry.boundsTree;
    // 多材质 groups 的遍历顺序属于原生 tie-break；先保守保留原生精确层，仍享受槽位粗筛。
    const useGeometryBvh = tree instanceof MeshBVH && tree !== this.staleBoundsTree && !Array.isArray(object.material);
    const hadFirstHitOnly = Object.prototype.hasOwnProperty.call(raycaster, 'firstHitOnly');
    const firstHitOnly = raycaster.firstHitOnly;
    const near = raycaster.near;
    const far = raycaster.far;
    try {
      // 原生 InstancedMesh 不截断每槽后方命中；剖面过滤需要保留它们，不能套用 Mesh 的 nearest-only。
      if (useGeometryBvh) {
        raycaster.firstHitOnly = false;
        // BVH 的缩放长度换算不覆盖剪切矩阵；先求局部命中，再按真实世界距离应用 near/far。
        raycaster.near = 0;
        raycaster.far = Infinity;
      }
      for (const instanceId of this.candidates) {
        this.lastPreciseTests++;
        this.preciseTests++;
        object.getMatrixAt(instanceId, this.instanceMatrix);
        preciseMesh.matrixWorld.multiplyMatrices(object.matrixWorld, this.instanceMatrix);
        if (useGeometryBvh) {
          acceleratedRaycast.call(preciseMesh, raycaster, this.preciseHits);
          this.preciseHits.sort(InstancedMeshRaycastIndex.compareFaceIndex);
        } else {
          Mesh.prototype.raycast.call(preciseMesh, raycaster, this.preciseHits);
        }
        for (const hit of this.preciseHits) {
          if (hit.distance < near || hit.distance > far) continue;
          hit.instanceId = instanceId;
          hit.object = object;
          intersects.push(hit);
        }
        this.preciseHits.length = 0;
      }
    } finally {
      this.preciseHits.length = 0;
      if (useGeometryBvh) {
        raycaster.near = near;
        raycaster.far = far;
        if (hadFirstHitOnly) raycaster.firstHitOnly = firstHitOnly;
        else delete raycaster.firstHitOnly;
      }
    }
  };

  /** @param instanceId 需要重算局部边界的实际变化槽位。 */
  private refreshInstanceBounds(instanceId: number): void {
    const object = this.object!;
    object.getMatrixAt(instanceId, this.instanceMatrix);
    const geometryBounds = object.geometry.boundingBox;
    if (geometryBounds) {
      this.bounds.copy(geometryBounds).applyMatrix4(this.instanceMatrix);
      // 只扩粗筛边界应对逆变换的舍入；不改变精确拾取半径、面容差或实际顶点。
      if (!this.bounds.isEmpty()) {
        const extent = Math.max(1, Math.abs(this.bounds.min.x), Math.abs(this.bounds.min.y), Math.abs(this.bounds.min.z),
          Math.abs(this.bounds.max.x), Math.abs(this.bounds.max.y), Math.abs(this.bounds.max.z));
        if (Number.isFinite(extent)) this.bounds.expandByScalar(extent * Number.EPSILON * 64);
      }
      this.index.setBounds(instanceId, this.bounds);
    } else {
      this.index.setBounds(instanceId, null);
    }
    this.refreshedBounds++;
  }

  /**
   * 检测会改变共享 BVH 构建图元范围的元数据；未重建的旧树只用于保守回退。
   * @param geometry 当前实例组几何。
   */
  private refreshGeometryRange(geometry: BufferGeometry): void {
    let changed = this.drawStart !== geometry.drawRange.start || this.drawCount !== geometry.drawRange.count
      || this.geometryGroups.length !== geometry.groups.length * 3;
    for (let index = 0; index < geometry.groups.length; index++) {
      const group = geometry.groups[index];
      const offset = index * 3;
      if (this.geometryGroups[offset] !== group.start || this.geometryGroups[offset + 1] !== group.count
        || this.geometryGroups[offset + 2] !== (group.materialIndex ?? 0)) changed = true;
    }
    if (!changed) return;
    if (this.geometry === geometry) this.staleBoundsTree = geometry.boundsTree;
    this.drawStart = geometry.drawRange.start;
    this.drawCount = geometry.drawRange.count;
    this.geometryGroups.length = geometry.groups.length * 3;
    for (let index = 0; index < geometry.groups.length; index++) {
      const group = geometry.groups[index];
      this.geometryGroups[index * 3] = group.start;
      this.geometryGroups[index * 3 + 1] = group.count;
      this.geometryGroups[index * 3 + 2] = group.materialIndex ?? 0;
    }
  }

  /** @param left 左命中。 @param right 右命中。 @returns 原生单材质 Mesh 的三角遍历顺序。 */
  private static compareFaceIndex(left: Intersection, right: Intersection): number {
    return (left.faceIndex ?? -1) - (right.faceIndex ?? -1);
  }
}
